import { Select } from "@mantine/core";
import { DatePicker, TimeInput } from "@mantine/dates";
import "dayjs/locale/it";
import { useAtom, useAtomValue } from "jotai";
import { TFunction } from "react-i18next";
import { useState, useEffect, useCallback } from "react";

import Button from "components/Button";
import Flex from "components/Flex";
import PageLayout from "components/PageLayout";
import Text from "components/Text";

import { useGetCitiesByProvinceAndCountry } from "app/hooks/useGetCitiesByProvinceAndCountry";
import { cityOfAccidentAtom, countryOfAccidentAtom, provinceOfAccidentAtom } from "atoms/accidentLocation";
import { clientConfigAtom } from "atoms/clientConfig";
import { dateOfAccidentAtom, timeOfAccidentAtom } from "atoms/timeOfAccident";
import { useClientTranslation } from "hooks/useClientTranslation";
import { useDigitalJourneyPageRouting } from "hooks/useDigitalJourneyPageRouting";
import { useEventTracking } from "hooks/useEventTracking";
import { getProvinces } from "utils/digitalJourney/provinces";

export const DJAccidentTimeAndLocationPage = () => {
  const { t } = useClientTranslation();
  const { trackEvent } = useEventTracking();
  const { onNext, continueCTA } = useDigitalJourneyPageRouting();
  const [dateOfAccident, setDateOfAccident] = useAtom(dateOfAccidentAtom);
  const [timeOfAccident, setTimeOfAccident] = useAtom(timeOfAccidentAtom);
  const [countryOfAccident, setCountryOfAccident] = useAtom(countryOfAccidentAtom);
  const [provinceOfAccident, setProvinceOfAccident] = useAtom(provinceOfAccidentAtom);
  const [cityOfAccident, setCityOfAccident] = useAtom(cityOfAccidentAtom);
  const { clientId } = useAtomValue(clientConfigAtom);
  const cities = useGetCitiesByProvinceAndCountry(provinceOfAccident, countryOfAccident);
  const provinces = getProvinces(clientId, countryOfAccident || "IT");
  const [dateTimeError, setDateTimeError] = useState<string | null>(null);

  const getIsInvalidDate = (date: Date | string | null) => {
    if (date && date instanceof Date && !isNaN(date.getTime())) return false;
    if (date && typeof date === "string" && !isNaN(new Date(date).getTime())) return false;
    return true;
  };

  // Check if the selected date and time are in the future
  const isDateTimeInFuture = useCallback(() => {
    if (!dateOfAccident || !timeOfAccident) return false;
    
    const selectedDate = typeof dateOfAccident === 'string' ? new Date(dateOfAccident) : dateOfAccident;
    const selectedTime = typeof timeOfAccident === 'string' ? new Date(timeOfAccident) : timeOfAccident;
    
    if (getIsInvalidDate(selectedDate) || getIsInvalidDate(selectedTime)) return false;
    
    // Combine date and time
    const combinedDateTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      selectedTime.getHours(),
      selectedTime.getMinutes(),
      selectedTime.getSeconds()
    );
    
    return combinedDateTime > new Date();
  },[dateOfAccident, timeOfAccident]);

  useEffect(() => {
    if (isDateTimeInFuture()) {
      setDateTimeError(t("digital_journey.accident_location.datetime_future_error"));
    } else {
      setDateTimeError(null);
    }
  }, [dateOfAccident, timeOfAccident, t, isDateTimeInFuture]);

  const isDisabled =
    getIsInvalidDate(dateOfAccident) ||
    getIsInvalidDate(timeOfAccident) ||
    !countryOfAccident ||
    !provinceOfAccident ||
    !cityOfAccident ||
    isDateTimeInFuture();

  const onContinue = () => {
    trackEvent({ name: "Digital Journey Accident Time and Location - continue" });
    if (!isDisabled) onNext();
  };

  const handleDateChange = (date: Date | null) => {
    setDateOfAccident(date);
  };

  const handleTimeChange = (date: Date | null) => {
    setTimeOfAccident(date);
  };

  return (
    <PageLayout contentSpacing="space-between">
      <Flex px={24} py={24} gap={16} flex={1} mb={16}>
        <Text as="h1" sizeVariant="xl" weight={600}>
          {t("digital_journey.accident_location.title")}
        </Text>
        <DatePicker
          locale="it"
          placeholder={t("digital_journey.accident_location.placeholder")}
          label={t("digital_journey.accident_location.date_of_accident")}
          maxDate={new Date()}
          value={typeof dateOfAccident === "string" ? new Date(dateOfAccident) : dateOfAccident}
          onChange={handleDateChange}
          error={dateTimeError && dateOfAccident ? dateTimeError : undefined}
        />
        <TimeInput
          mb={24}
          placeholder={t("digital_journey.accident_location.placeholder")}
          label={t("digital_journey.accident_location.time_of_accident")}
          value={typeof timeOfAccident === "string" ? new Date(timeOfAccident) : timeOfAccident}
          onChange={handleTimeChange}
          error={dateTimeError && timeOfAccident ? dateTimeError : undefined}
        />
        <Select
          label={t("digital_journey.accident_location.country_of_accident")}
          placeholder={t("digital_journey.accident_location.placeholder")}
          data={getCountries(t)}
          value={countryOfAccident}
          onChange={(value) => {
            if (value !== countryOfAccident) setProvinceOfAccident(null);
            setCountryOfAccident(value);
          }}
        />
        <Select
          disabled={!countryOfAccident}
          label={t("digital_journey.accident_location.province_of_accident")}
          placeholder={t("digital_journey.accident_location.placeholder")}
          data={(provinces || []).map((province) => ({ value: province, label: province }))}
          value={provinceOfAccident}
          onChange={(value) => {
            setProvinceOfAccident(value);
            if (value !== provinceOfAccident) {
              setCityOfAccident(null);
            }
          }}
        />
        <Select
          disabled={!provinceOfAccident}
          label={t("digital_journey.accident_location.city_of_accident")}
          placeholder={t("digital_journey.accident_location.placeholder")}
          data={(cities || []).map((city) => ({ value: city.id.toString(), label: city.city }))}
          value={cityOfAccident ? cityOfAccident.id.toString() : null}
          onChange={(value) => {
            if (value !== null) {
              const id = Number(value);
              const city = cities.find((city) => city.id === id);
              setCityOfAccident(city || null);
            }
          }}
        />
      </Flex>
      <Flex px={24} py={24} gap={16}>
        <Button disabled={isDisabled} onClick={onContinue}>
          {continueCTA}
        </Button>
      </Flex>
    </PageLayout>
  );
};

export const getCountries = (t: TFunction) => {
  return [
    { label: t("digital_journey.accident_location.country_of_accident.italy"), value: "IT" },
    {
      label: t("digital_journey.accident_location.country_of_accident.outside_of_italy"),
      value: "Other",
    },
  ];
};